import { useEffect } from "react";

type Args = {
  undo: () => void;
  redo: () => void;
  // Set false to disable — callers that mount inside text-heavy
  // surfaces (e.g. a builder that already wires its own undo) can
  // opt out without unmounting the host component.
  enabled?: boolean;
};

/**
 * Cmd+Z (undo) / Cmd+Shift+Z (redo). Ctrl on non-mac.
 * Skips when focus is in an input / textarea / contenteditable so we
 * don't hijack text-field undo.
 */
export const useUndoRedoKeystrokes = ({
  undo,
  redo,
  enabled = true,
}: Args): void => {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== "z") return;
      const target = e.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, enabled]);
};
